import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getEvent, addResponse } from '../services/api';
import { useLang } from '../contexts/LanguageContext';
import '../styles/evaluationPage.css';

const CONTENT = {
  en: {
    scaleTitle: 'Rating Scale:',
    scaleDesc: 'Please rate each question on a scale from 1 to 5:',
    scaleLabels: ['1 = Strongly Disagree', '2 = Disagree', '3 = Neutral', '4 = Agree', '5 = Strongly Agree'],
    questionLabel: (i) => `Question ${i + 1}:`,
    submit: 'Submit Responses',
    thanks: 'Thank you!',
    thanksDesc: 'Your responses have been successfully submitted.',
    loading: 'Loading...',
    notFound: 'Event not found',
    alreadySubmitted: 'You have already submitted your responses for this event.',
    submitError: 'Error submitting responses. Please try again.',
  },
  pt: {
    scaleTitle: 'Escala de Avaliação:',
    scaleDesc: 'Por favor, avalie cada questão numa escala de 1 a 5:',
    scaleLabels: ['1 = Discordo Totalmente', '2 = Discordo', '3 = Neutro', '4 = Concordo', '5 = Concordo Totalmente'],
    questionLabel: (i) => `Questão ${i + 1}:`,
    submit: 'Enviar Respostas',
    thanks: 'Obrigado!',
    thanksDesc: 'As suas respostas foram enviadas com sucesso.',
    loading: 'A carregar...',
    notFound: 'Evento não encontrado',
    alreadySubmitted: 'Já enviou as suas respostas para este evento.',
    submitError: 'Erro ao enviar respostas. Por favor, tente novamente.',
  },
};

const EvaluationPage = () => {
  const { id } = useParams();
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;

  const [event, setEvent] = useState(null);
  const [responses, setResponses] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        const eventData = await getEvent(id);
        setEvent(eventData);
        setResponses(eventData.questions.map((_, index) => ({ questionIndex: index, score: 0 })));
      } catch (err) {
        console.error('Error loading event:', err);
        setError(c.notFound);
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      e.target.querySelector('button[type="submit"]').disabled = true;
      await addResponse(id, responses);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.message && err.message.includes('já respondeu')) {
        setError(c.alreadySubmitted);
      } else {
        setError(c.submitError);
      }
      console.error('Error submitting responses:', err);
      e.target.querySelector('button[type="submit"]').disabled = false;
    }
  };

  if (loading) return <div className="loading">{c.loading}</div>;
  if (error && !event) return <div className="error-page">{error}</div>;
  if (!event) return <div className="error-page">{c.notFound}</div>;

  if (submitted) {
    return (
      <div className="success-message">
        <h2>{c.thanks}</h2>
        <p>{c.thanksDesc}</p>
      </div>
    );
  }

  return (
    <div className="evaluation-container">
      <div className="evaluation-content">
        <h2>{event.title}</h2>

        <div className="scale-explanation">
          <h3>{c.scaleTitle}</h3>
          <p>{c.scaleDesc}</p>
          <div className="scale-details">
            {c.scaleLabels.map((label, i) => (
              <span key={i}>{label}</span>
            ))}
          </div>
        </div>

        {error && <div className="error-inline">{error}</div>}

        <form onSubmit={handleSubmit}>
          {event.questions.map((question, index) => (
            <div key={index} className="question-container">
              <h4>{c.questionLabel(index)}</h4>
              <p className="question-text">{question}</p>
              <div className="rating-container">
                {[1, 2, 3, 4, 5].map((score) => (
                  <label key={score} className={`rating-label${responses[index]?.score === score ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name={`question-${index}`}
                      value={score}
                      checked={responses[index]?.score === score}
                      onChange={() => {
                        const newResponses = [...responses];
                        newResponses[index] = { questionIndex: index, score };
                        setResponses(newResponses);
                      }}
                      required
                    />
                    <span className="rating-number">{score}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button type="submit" className="submit-button">
            {c.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EvaluationPage;
